import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { endpointFor, type EntityKey } from '@/lib/api/endpoints'

/**
 * A reference list that the server returns as a plain array.
 *
 * <p>Distinct from {@link useListQuery}, which expects a `PageResponse` and reads `.content`
 * off it. The org lookups — departments, grades, locations, designations, cost centres,
 * legal entities — are complete lists with no paging, and calling them through the paged
 * hook silently yields `undefined`: every picker built that way renders an empty dropdown
 * with no error, because `data?.content` on an array is simply not there.
 *
 * <p>That is exactly what happened to the transfer wizard, whose five pickers had been empty
 * since they were built. Two hooks with names that say which shape they expect is what stops
 * it happening again.
 */
export function useLookup<T>(entityKey: EntityKey, enabled = true) {
  return useQuery({
    queryKey: [entityKey, 'lookup'],
    enabled,
    queryFn: async () => (await apiClient.get<T[]>(endpointFor(entityKey))).data,
    // Reference data changes when somebody edits the org structure, not between renders.
    staleTime: 5 * 60_000,
  })
}
